import { MenuPresets } from '@framework/form/inputs/rich-text/menu/MenuPresets.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { IconClick, IconColumns, IconListDetails } from '@tabler/icons-react';
import { Alert, Card } from 'antd';
import { type ReactNode, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { RichTextAdvancedInput } from '../../../../../../inputs/rich-text-advanced/RichTextAdvancedInput.tsx';
import { type IWidgetComponentProps } from '../../../../IWidgetType.ts';
import { type IWidgetConfigurationSlot } from '../../../common/config-slots/IWidgetConfigurationSlot.ts';
import { WidgetConfigurationSlotRender } from '../../../common/config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from '../../../common/config-slots/WidgetConfigurationSlots.tsx';
import { type IAdvancedMultiSelectWidget } from '../../advanced-multi-select/IAdvancedMultiSelectWidget.ts';
import { type IAdvancedSingleSelectWidget } from '../../advanced-single-select/IAdvancedSingleSelectWidget.ts';
import { CommonConfigComponent } from '../../common/CommonInputConfigComponent.tsx';
import { type IInputWidget } from '../../IInputWidget.ts';
import { type IAdvancedSelectColumns } from '../IAdvancedSelectColumns.ts';
import { AdvancedSelectTableConfigForm } from '../table/AdvancedSelectTableConfigForm.tsx';
import { AdvancedSelectImportCsvOptionsButton } from './AdvancedSelectImportCsvOptionsButton.tsx';
import {
  getAdvancedSelectColumnOptions,
  getAdvancedSelectUniqueValueColumnOptions,
} from './advancedSelectColumnOptions.ts';

/** Card "Configurazione Colonne": struttura delle opzioni degli advanced select. */
const AdvancedSelectColumnsSlot: IWidgetConfigurationSlot = {
  key: 'advanced-select-columns',
  title: 'Configurazione Colonne',
  icon: <TbIcon icon={IconColumns} />,
  description:
    'Colonne delle opzioni: struttura, valore univoco e colonne di etichetta.',
  order: 15,
};

/** Card "Configurazione Opzioni": righe/opzioni tabellari degli advanced select. */
const AdvancedSelectOptionsSlot: IWidgetConfigurationSlot = {
  key: 'advanced-select-options',
  title: 'Configurazione Opzioni',
  icon: <TbIcon icon={IconListDetails} />,
  description: 'Elenco delle opzioni selezionabili, per le colonne definite.',
  order: 16,
};

export interface IAdvancedSelectWidgetConfigComponentProps<
  TWidget extends IInputWidget,
> extends IWidgetComponentProps<TWidget> {
  /**
   * Titolo del pannello di configurazione (es: "Scelta Singola Avanzata", "Scelta Multipla Avanzata")
   */
  panelTitle: string;
  /**
   * Chiave del pannello di default
   */
  panelKey: string;
  /**
   * Componente per la lista delle opzioni
   */
  optionListComponent: ReactNode;
  /**
   * Testo di help per le colonne di visualizzazione del valore
   */
  labelFieldColumnsHelp?: string;
  /**
   * Componente per il default
   */
  defaultNode?: ReactNode;
}

/**
 * Componente di configurazione condiviso per i widget di selezione avanzata
 * (singola e multipla)
 */
export function AdvancedSelectWidgetConfigComponent<
  TWidget extends IAdvancedSingleSelectWidget | IAdvancedMultiSelectWidget,
>(props: IAdvancedSelectWidgetConfigComponentProps<TWidget>) {
  const {
    panelTitle,
    panelKey,
    optionListComponent,
    labelFieldColumnsHelp,
    ...widgetProps
  } = props;

  const columns = useWatch({ name: 'columns' }) as IAdvancedSelectColumns[];

  const allColumnsOptions = useMemo(
    () => getAdvancedSelectColumnOptions(columns),
    [columns],
  );

  const uniqueValueColumnOptions = useMemo(
    () => getAdvancedSelectUniqueValueColumnOptions(columns),
    [columns],
  );

  const hasColumns = useMemo(() => {
    return columns && columns.length > 0;
  }, [columns]);

  // Card "Impostazioni <widget>": titolo e key arrivano dal widget specifico
  // (Scelta Singola/Multipla Avanzata), quindi lo slot è costruito qui.
  const widgetSettingsSlot = useMemo<IWidgetConfigurationSlot>(
    () => ({
      key: panelKey,
      title: `Impostazioni ${panelTitle}`,
      icon: <TbIcon icon={IconClick} />,
      description: 'Testi dei pulsanti e della finestra modale di selezione.',
      order: 17,
    }),
    [panelKey, panelTitle],
  );

  return (
    <>
      <CommonConfigComponent {...widgetProps} hideConfigDefaultValue />
      {props.defaultNode && (
        <WidgetConfigurationSlotRender
          slot={WidgetConfigurationSlots.Default}
          order={10}
        >
          {props.defaultNode}
        </WidgetConfigurationSlotRender>
      )}
      <WidgetConfigurationSlotRender slot={AdvancedSelectColumnsSlot}>
        {!hasColumns && (
          <Alert
            message="Importa da CSV o configura manualmente"
            description={
              <>
                <p>
                  Puoi importare le opzioni da un CSV oppure configurare
                  manualmente le colonne nella tabella sottostante e aggiungere
                  le opzioni una per una.
                </p>
                <p>
                  <AdvancedSelectImportCsvOptionsButton />
                </p>
              </>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <AdvancedSelectTableConfigForm />
        <VerticalSpace size={12} />
        {columns && (
          <Card size="small" title="Configurazione avanzata colonne">
            <FormItem
              name="uniqueValueColumnId"
              label="Colonna per valore univoco"
              help="Seleziona la colonna per identificare univocamente ogni opzione."
            >
              <SelectInput
                name="uniqueValueColumnId"
                options={uniqueValueColumnOptions}
                placeholder="Seleziona la colonna..."
                allowClear
              />
            </FormItem>
            <FormItem
              name="labelFieldColumns"
              label="Colonne di Etichetta"
              help={
                labelFieldColumnsHelp ??
                'Seleziona le colonne da mostrare riguardo le opzioni selezionate.'
              }
            >
              <SelectInput
                name="labelFieldColumns"
                options={allColumnsOptions}
                placeholder="Seleziona le colonne di etichetta..."
                allowClear
                mode="multiple"
              />
            </FormItem>
            <FormItem
              name="selectedLabelFieldColumns"
              label="Colonne Visualizzate in Selezione"
              help={
                "Seleziona le colonne da mostrare nella tabella all'interno della finestra modale di selezione. Le colonne non selezionate qui rimarranno nascoste all'utente ma i loro valori verranno comunque registrati nel modulo quando viene selezionata un'opzione."
              }
            >
              <SelectInput
                name="selectedLabelFieldColumns"
                options={allColumnsOptions}
                placeholder="Seleziona le colonne di visualizzazione in selezione..."
                allowClear
                mode="multiple"
              />
            </FormItem>
          </Card>
        )}
      </WidgetConfigurationSlotRender>
      <WidgetConfigurationSlotRender slot={AdvancedSelectOptionsSlot}>
        {optionListComponent}
      </WidgetConfigurationSlotRender>
      <WidgetConfigurationSlotRender slot={widgetSettingsSlot}>
        <FormItem name="buttonText" label="Testo del pulsante di scelta">
          <TextInput
            name="buttonText"
            placeholder="Inserisci il testo del pulsante di scelta..."
          />
        </FormItem>
        <FormItem
          name="buttonTextAfter"
          label="Testo del pulsante di modifica dopo la scelta"
        >
          <TextInput
            name="buttonTextAfter"
            placeholder="Inserisci il testo del pulsante di modifica..."
          />
        </FormItem>
        <FormItem
          name="cancelButtonText"
          label="Testo del pulsante di cancellazione scelta"
        >
          <TextInput
            name="cancelButtonText"
            placeholder="Inserisci il testo del pulsante di cancellazione..."
          />
        </FormItem>
        <FormItem
          name="modalTitle"
          label="Titolo della finestra modale di selezione"
        >
          <RichTextAdvancedInput
            name="modalTitle"
            rows={2}
            menus={MenuPresets.Minimal}
          />
        </FormItem>
      </WidgetConfigurationSlotRender>
    </>
  );
}
